// gatsby-browser only supports common js modules
// require("./src/styles/pages.scss");

const { getCookie } = require("./src/utils/cookies");
const {
  getSegmentPageContext,
} = require("./src/utils/tracking/getSegmentPageContext");

/**
 * shouldUpdateScroll
 *
 * Fix for scroll position not resetting on first navigation after SSR.
 * Uses setTimeout to defer scroll until after Gatsby's internal scroll handling.
 *
 * Behavior:
 * - Forward navigation (clicking links): Scrolls to top [0, 0]
 * - Back/forward buttons: Restores saved scroll position
 *
 * @see https://github.com/gatsbyjs/gatsby/issues/38201#issuecomment-1631378720
 *
 * @param {Object} args
 * @param {Object} args.routerProps - Router props containing location
 * @param {Function} args.getSavedScrollPosition - Function to get saved scroll position
 * @returns {boolean} - Always false to prevent Gatsby's default scroll behavior
 */
/**
 * Scrolls to the element matching a URL hash. Re-applies the scroll a few
 * times because images/videos above the target load after the first scroll and
 * shift the layout, leaving the target out of view.
 */
const scrollToHash = (hash) => {
  const id = decodeURIComponent(hash.slice(1));
  [0, 300, 900].forEach((delay) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView();
      }
    }, delay);
  });
};

exports.shouldUpdateScroll = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  if (location.hash) {
    scrollToHash(location.hash);
    return false;
  }

  const currentPosition = getSavedScrollPosition(location);

  setTimeout(() => {
    window.scrollTo(...(currentPosition || [0, 0]));
  }, 0);

  return false;
};

exports.onInitialClientRender = () => {
  if (window.location.hash) {
    scrollToHash(window.location.hash);
  }
};

// When the Klaviyo outage fallback is active, Klaviyo's script clears
// embedded form content on client-side URL changes but doesn't re-initialize
// divs already in the DOM. Force a full page reload on navigation so Klaviyo
// gets a fresh page load and re-initializes all forms from scratch.
// This trades SPA performance for correctness during an outage — acceptable
// since the flag is only ever enabled temporarily as an emergency measure.
exports.onPreRouteUpdate = ({ location, prevLocation }) => {
  // prevLocation is null on initial hydration — skip to avoid a redirect loop.
  // Only force a reload on actual client-side navigations.
  if (!prevLocation) return;
  if (process.env.GATSBY_KLAVIYO_OUTAGE_FALLBACK_ENABLED === "true") {
    window.location.href =
      location.pathname + location.search + location.hash;
  }
};

// Segment's page/pageview calls used to fire once from the SSR-injected
// <head> script (gatsby-ssr.js), which only runs on a full document load.
// Gatsby's client-side nav never reloads the document, so in-app navigation
// never re-fired them. onRouteUpdate fires on the initial load and on every
// client-side transition, so it fully replaces those calls.
exports.onRouteUpdate = ({ prevLocation }) => {
  if (typeof window === "undefined" || !window.analytics) return;

  const screenWidth = window.innerWidth;
  // Google Analytics' standard device breakpoints -- intentionally matches
  // the same classification already used for click events (see
  // src/utils/tracking/gtm-events.js) rather than this site's CSS layout
  // breakpoints (src/styles/_variables.scss), which serve a different
  // purpose and would put pageview and click events in different buckets.
  let deviceType;
  if (screenWidth >= 1200) {
    deviceType = "desktop";
  } else if (screenWidth >= 768) {
    deviceType = "tablet";
  } else {
    deviceType = "mobile";
  }

  const utmSource = getCookie("later_mktg_utm_source") || undefined;

  // document.referrer never updates on a client-side route change -- it
  // stays pinned to whatever referred the very first document load. On a
  // client-side navigation (prevLocation is set), build the actual in-app
  // previous page's URL instead; prevLocation is null on initial hydration,
  // where document.referrer's default (the true external referrer) is
  // already correct.
  const inAppReferrer = prevLocation
    ? `${window.location.origin}${prevLocation.pathname}${prevLocation.search}${prevLocation.hash}`
    : undefined;

  const fireEvents = () => {
    // Segment's classic snippet queues calls made before the real library
    // finishes loading and freezes a page-context snapshot at queue time,
    // replaying it later regardless of what's passed via the `context`
    // option -- unreliable for a client-side route change, since whether a
    // given call gets queued (and which snapshot it's frozen with) depends
    // on load timing. properties.* is unaffected by that buffering, so put
    // the current page's fields there as the reliable source of truth.
    // Also pass context.page as a best effort -- confirmed not to fix
    // context.page on its own, but harmless, and may still help on
    // page()-type events or specific destinations.
    const pageContext = getSegmentPageContext({ referrer: inAppReferrer });
    const options = { context: { page: pageContext } };

    window.analytics.page({ ...pageContext, deviceType }, options);
    window.analytics.track(
      "viewed-webpage",
      { ...pageContext, deviceType, source: utmSource },
      options
    );
  };

  // react-helmet defers the actual <title> DOM commit to
  // requestAnimationFrame (handleClientStateChange in
  // react-helmet/lib/Helmet.js), so a plain macrotask -- setTimeout(0) --
  // isn't guaranteed to run after it: a zero-delay timeout is commonly
  // clamped shorter than a frame and can fire before the next rAF.
  // Watching document.head for any mutation was also too broad -- Gatsby
  // injects <link rel="prefetch"> tags into head synchronously on every
  // client-side nav, before Helmet's rAF-deferred commit, which settled
  // fireEvents() early with the outgoing page's still-current title.
  // react-helmet sets document.title directly, which does mutate the
  // <title> element's text node, so watch that element specifically --
  // <link> insertions elsewhere in head no longer trigger a false settle.
  let settled = false;
  const settle = () => {
    if (settled || !document.title) return;
    settled = true;
    observer.disconnect();
    clearTimeout(timeoutId);
    fireEvents();
  };
  const forceSettle = () => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    fireEvents();
  };

  const titleEl = document.querySelector("title") || document.head;
  const observer = new MutationObserver(settle);
  observer.observe(titleEl, {
    subtree: true,
    childList: true,
    characterData: true,
  });
  const timeoutId = setTimeout(forceSettle, 500);
};

exports.onClientEntry = () => {
  if (!window.ChiliPiper) {
    const script = document.createElement("script");
    script.id = "chilipiper-concierge";
    script.src = "https://later.chilipiper.com/concierge-js/cjs/concierge.js";
    script.crossOrigin = "anonymous";
    script.onload = function () {
      console.log("Chili Piper loaded!");
      window.ChiliPiper.isReady = true;
    };
    document.body.appendChild(script);
  }
};
